import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { AIBrainLoader, AIProgressLoader } from '../components/ui/ai-loading';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, Shield,
  Brain, TrendingUp, TrendingDown, Clock, CheckCircle,
  AlertCircle, Heart, Stethoscope, FileText, Bell,
} from 'lucide-react';
import { DashboardShell } from '../components/brand/DashboardShell';
import { clinicianNavItems } from '../components/brand/navItems';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RiskAssessment() {
  const { patientId } = useParams();
  const { token } = useAuth();
  const location = useLocation();
  const [assessment, setAssessment] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPredictions, setLoadingPredictions] = useState(false);

  useEffect(() => {
    if (patientId) {
      fetchHistory();
    }
  }, [patientId]);

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/risk/history/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setHistory(await response.json());
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const runAssessment = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/risk/assess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ patient_id: patientId })
      });

      if (response.ok) {
        const data = await response.json();
        setAssessment(data);
        toast.success('Risk assessment complete');
        fetchHistory();
      } else {
        toast.error('Assessment failed');
      }
    } catch (error) {
      toast.error('Error running assessment');
    } finally {
      setLoading(false);
    }
  };

  const fetchPredictions = async () => {
    setLoadingPredictions(true);
    try {
      const response = await fetch(`${API_URL}/api/predictions/patient/${patientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPredictions(data);
        toast.success('Predictions generated');
      } else {
        toast.error('Failed to generate predictions');
      }
    } catch (error) {
      toast.error('Error fetching predictions');
    } finally {
      setLoadingPredictions(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'emerald';
      case 'moderate': return 'amber';
      case 'high': return 'orange';
      case 'critical': return 'red';
      default: return 'slate';
    }
  };

  const getRiskIcon = (level) => {
    switch (level) {
      case 'low': return CheckCircle;
      case 'moderate': return AlertCircle;
      case 'high': return AlertTriangle;
      case 'critical': return AlertTriangle;
      default: return Shield;
    }
  };

  const getTrajectoryIcon = (trajectory) => {
    switch (trajectory) {
      case 'improving': return TrendingUp;
      case 'stable': return Activity;
      case 'declining': return TrendingDown;
      case 'needs_attention': return AlertTriangle;
      default: return Activity;
    }
  };

  return (
    <DashboardShell
      navItems={clinicianNavItems({ pathname: location.pathname })}
      title="AI Risk Assessment"
      subtitle={`Patient ID: ${patientId?.slice(0, 8)}...`}
      testId="risk-assessment"
    >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-4 mb-8">
          <div className="flex gap-3">
            <Button
              onClick={runAssessment}
              disabled={loading}
              className="bg-gradient-to-r from-cn-primary to-cn-sage-500 hover:from-cn-primary hover:to-cn-sage-600 text-white rounded-full px-6"
              data-testid="run-assessment-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Brain className="w-5 h-5 mr-2" />
              )}
              Run Assessment
            </Button>
            <Button
              onClick={fetchPredictions}
              disabled={loadingPredictions}
              variant="outline"
              className="rounded-full px-6"
              data-testid="get-predictions-btn"
            >
              {loadingPredictions ? (
                <div className="w-5 h-5 border-2 border-cn-primary border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <TrendingUp className="w-5 h-5 mr-2" />
              )}
              Get Predictions
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Current Assessment */}
          <div className="space-y-6">
            {assessment ? (
              <>
                {/* Risk Level Card */}
                <Card className={`bg-${getRiskColor(assessment.risk_level)}-50 dark:bg-${getRiskColor(assessment.risk_level)}-900/20 border-0`}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Risk Level</p>
                        <p className={`text-3xl font-bold text-${getRiskColor(assessment.risk_level)}-600 dark:text-${getRiskColor(assessment.risk_level)}-400 font-display capitalize mt-1`}>
                          {assessment.risk_level}
                        </p>
                      </div>
                      <div className={`w-16 h-16 bg-${getRiskColor(assessment.risk_level)}-100 dark:bg-${getRiskColor(assessment.risk_level)}-900/50 rounded-2xl flex items-center justify-center`}>
                        {React.createElement(getRiskIcon(assessment.risk_level), {
                          className: `w-8 h-8 text-${getRiskColor(assessment.risk_level)}-600 dark:text-${getRiskColor(assessment.risk_level)}-400`
                        })}
                      </div>
                    </div>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-slate-600 dark:text-slate-400">Risk Score</span>
                        <span className="font-bold">{assessment.risk_score}/100</span>
                      </div>
                      <div className="w-full bg-slate-200 dark:bg-cn-ink-700 rounded-full h-3">
                        <div
                          className={`bg-${getRiskColor(assessment.risk_level)}-500 h-3 rounded-full transition-all`}
                          style={{ width: `${assessment.risk_score}%` }}
                        />
                      </div>
                    </div>
                    {assessment.alert_clinician && (
                      <div className="mt-4 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center gap-2">
                        <Bell className="w-5 h-5 text-red-600 dark:text-red-400" />
                        <span className="text-sm font-medium text-red-700 dark:text-red-300">
                          Immediate clinician attention recommended
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Risk Factors */}
                <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                      Risk Factors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {assessment.risk_factors.map((factor, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">{factor}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Recommendations */}
                <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Stethoscope className="w-5 h-5 text-cn-primary" />
                      Recommendations
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {assessment.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-cn-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Interventions */}
                <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="w-5 h-5 text-red-500" />
                      Suggested Interventions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {assessment.suggested_interventions.map((intervention, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-cn-primary mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-slate-700 dark:text-slate-300">{intervention}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </>
            ) : loading ? (
              /* AI Loading State */
              <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg overflow-hidden">
                <CardContent className="py-8">
                  <AIBrainLoader message="Analyzing patient risk factors..." />
                  <AIProgressLoader 
                    className="mt-8"
                    steps={[
                      "Gathering patient data",
                      "Analyzing health history",
                      "Calculating risk scores",
                      "Generating recommendations"
                    ]}
                    currentStep={1}
                    progress={35}
                  />
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
                <CardContent className="py-16 text-center">
                  <Brain className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500 dark:text-slate-400">
                    Click "Run Assessment" to analyze patient risk
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Predictions & History */}
          <div className="space-y-6">
            {/* AI Predictions Loading State */}
            {loadingPredictions && (
              <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg overflow-hidden">
                <CardContent className="py-8">
                  <AIBrainLoader message="Generating care predictions..." />
                  <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-4">
                    Analyzing patient trajectory and forecasting needs...
                  </p>
                </CardContent>
              </Card>
            )}
            
            {/* AI Predictions */}
            {predictions && !loadingPredictions && (
              <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-cn-accent" />
                    AI Care Predictions
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Trajectory */}
                  <div className="p-4 bg-slate-50 dark:bg-cn-ink-700/50 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">Care Trajectory</p>
                        <p className="text-xl font-bold text-slate-900 dark:text-white capitalize mt-1">
                          {predictions.care_trajectory}
                        </p>
                      </div>
                      {React.createElement(getTrajectoryIcon(predictions.care_trajectory), {
                        className: `w-8 h-8 ${
                          predictions.care_trajectory === 'improving' ? 'text-cn-primary' :
                          predictions.care_trajectory === 'stable' ? 'text-cn-primary' :
                          'text-amber-500'
                        }`
                      })}
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                      Confidence: {predictions.confidence_score}%
                    </p>
                  </div>

                  {/* Predicted Needs */}
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                      Predicted Needs (Next 30 Days)
                    </h4>
                    <ul className="space-y-1">
                      {predictions.predicted_needs.map((need, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <div className="w-1.5 h-1.5 bg-cn-accent rounded-full" />
                          {need}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Appointment Recommendation */}
                  <div className="p-3 bg-cn-sage-50 dark:bg-cn-sage-900/30 rounded-lg">
                    <p className="text-sm font-medium text-cn-primary-hover dark:text-cn-sage-300">
                      Recommended Follow-up: {predictions.appointment_recommendation}
                    </p>
                  </div>

                  {/* Specialist Referrals */}
                  {predictions.specialist_referrals?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                        Specialist Referrals
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {predictions.specialist_referrals.map((spec, i) => (
                          <span key={i} className="px-3 py-1 bg-cn-terracotta-100 dark:bg-cn-terracotta-700/40 text-cn-terracotta-600 dark:text-cn-terracotta-300 rounded-full text-sm">
                            {spec}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Insights */}
                  <div>
                    <h4 className="font-medium text-slate-900 dark:text-white mb-2">Key Insights</h4>
                    <ul className="space-y-2">
                      {predictions.insights.map((insight, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-400">
                          <Brain className="w-4 h-4 text-cn-accent mt-0.5 flex-shrink-0" />
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Assessment History */}
            <Card className="bg-white dark:bg-cn-ink-800 border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-cn-primary" />
                  Assessment History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history.length === 0 ? (
                  <p className="text-center text-slate-500 dark:text-slate-400 py-8">
                    No previous assessments
                  </p>
                ) : (
                  <div className="space-y-3">
                    {history.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-cn-ink-700/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full bg-${getRiskColor(item.risk_level)}-500`} />
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-white capitalize">
                              {item.risk_level} Risk
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              Score: {item.risk_score}/100
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {new Date(item.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
    </DashboardShell>
  );
}
